import React from 'react';
import { Alert, Box, Button, TextField, Typography } from '@mui/material';
import LoadingButton from '@mui/lab/LoadingButton';
import { useTranslation } from '../context/Internationalization';
import { Modal, ModalActions } from './Modal';
import ModalBody from './Modal/ModalBody';
import ModalTitle from './Modal/ModalTitle';
import EmailCodeInput from '../pages/Login/EmailCodeInput';
import useEmailChangeCode from './useEmailChangeCode';

const SmsLoginEmailPromptModal = ({ open = false, onClose = () => {}, user, language }) => {
  const { t } = useTranslation();
  const flow = useEmailChangeCode({ open, userId: user?._id, language, onClose });
  const title = user?.email ? t('change_email') : t('add_email_title');
  const close = () => { if (!flow.busy && !flow.verified) onClose(); };
  const remindLater = () => {
    localStorage.setItem('emailPromptRemindAfter', String(Date.now() + 24 * 60 * 60 * 1000));
    close();
  };
  return (
    <Modal open={open} onClose={close} type="info" ariaLabel={title} closeLabel={t('close')}>
      <form onSubmit={flow.challenge ? flow.verify : flow.send}>
        <ModalTitle title={flow.challenge ? t('email_change_verify_title') : title} />
        <ModalBody>
          <Box sx={{ width: '100%', pt: flow.challenge ? 1 : 0 }}>
            {flow.error && <Alert severity="error" sx={{ mb: 2 }}>{t(flow.verified ? 'email_change_sync_retry_notice' : flow.error)}</Alert>}
            {flow.blocked && <Alert severity="warning" sx={{ mb: 2 }}>{t('email_change_impersonation_forbidden')}</Alert>}
            {flow.challenge ? <>
              <Typography id="email-change-code-help" variant="body2" sx={{ mb: 2, overflowWrap: 'anywhere' }}>
                {t('email_code_sent_to')} <strong>{flow.challenge.email}</strong>. {!flow.verified && t('email_code_expiry_help')}
              </Typography>
              <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                <EmailCodeInput value={flow.code} onChange={flow.setCode} disabled={flow.busy || flow.blocked || flow.expired || flow.verified}
                  label={t('email_code_label')} error={Boolean(flow.error)} descriptionId="email-change-code-help" />
              </Box>
              {flow.expired && <Alert severity="warning" sx={{ mt: 2 }}>{t('email_code_expired')}</Alert>}
              <Box sx={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'space-between', mt: 2 }}>
                <Button onClick={flow.send} disabled={flow.busy || flow.blocked || flow.resendIn > 0 || flow.verified}>
                  {flow.resendIn ? `${t('email_code_resend_in')} ${flow.resendIn}s` : t('email_code_resend')}
                </Button>
                <Button onClick={flow.edit} disabled={flow.busy || flow.verified}>{t('email_change_edit_address')}</Button>
              </Box>
            </> : <>
              {user?.email && <Typography variant="body2" sx={{ mb: 2 }}>{t('current_email')}: {user.email}</Typography>}
              <Typography variant="body2" sx={{ mb: 2 }}>{t('email_change_code_notice')}</Typography>
              <TextField autoFocus fullWidth required type="email" label={user?.email ? t('new_email') : t('email')}
                value={flow.email} disabled={flow.busy || flow.blocked} onChange={event => flow.setEmail(event.target.value)} />
              {flow.resendIn > 0 && <Typography variant="body2" sx={{ mt: 1 }}>
                {t('email_code_resend_in')} {flow.resendIn}s
              </Typography>}
            </>}
          </Box>
        </ModalBody>
        <ModalActions>
          {!user?.email && !flow.challenge && <Button variant="outlined" fullWidth onClick={remindLater} disabled={flow.busy}>
            {t('remind_me_later')}
          </Button>}
          <Button variant="outlined" fullWidth onClick={close} disabled={flow.busy || flow.verified}>{t('cancel')}</Button>
          <LoadingButton type="submit" fullWidth loading={flow.busy}
            disabled={flow.blocked || (flow.challenge ? !flow.verified && (flow.code.length !== 6 || flow.expired) : flow.resendIn > 0)}>
            {flow.verified ? t('retry_synchronization') : flow.challenge ? t('email_change_verify_save') : t('email_change_send_code')}
          </LoadingButton>
        </ModalActions>
      </form>
    </Modal>
  );
};
export default SmsLoginEmailPromptModal;
